import { useState, useEffect } from "react";
import {
  Autocomplete,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import { default as axios } from "../axiosConfig";
import PhoneNumberInput from "./PhoneNumberInput";
import {
  API_ListCompanies,
  API_GetCompanyInfo,
  API_UpdateCompanyInfo,
  API_CreateCompany,
  API_ListHMs,
  API_UpdateHMInfo,
  API_CreateHM,
  API_ListJobContracts,
  API_GetJobs,
  API_GetCandidates,
  API_UpdateJobContract,
  API_CreateJobContract,
  API_CreateCompanyPosting,
  API_UpdateCompanyPosting,
  API_GetCompanyPosting,
} from "../Assets/ApiUrlContainer";
import dayjs from "dayjs";
import { Navigate } from "react-router-dom";

export const Projects = ({ isAdmin }) => {
  // Company Info
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState({
    Name: "",
    Address: "",
    PhoneNumber: "",
    FinancialControllerName: "",
    FinancialControllerEmail: "",
    FinancialControllerNumber: "",
  });

  // Hiring Manager Info
  const [hiringManagers, setHiringManagers] = useState([]);
  const [selectedHM, setSelectedHM] = useState(null);
  const [hmDetails, setHMDetails] = useState({
    Name: "",
    Email: "",
    PhoneNumber: "",
    CompanyId: "",
  });
  const [selectedAlias, setSelectedAlias] = useState("");

  // Job Contract Info
  const [jobContracts, setJobContracts] = useState([]);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractDetails, setContractDetails] = useState({
    JobId: "",
    CandidateId: "",
    ContractSigned: "",
    ContractCost: "",
    TotalContractCost: "",
    ContractorCost: "",
    ContractEndDate: "",
    WeeklyHoursTarget: 37.5,
    DailyHoursLimit: 7.5,
    Currency: "CAD"
  });
  const [postingDetails, setPostingDetails] = useState({
    CompanyId: "",
    JobId: "",
    ManagerId: "",
    AliasId: "",
  });
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [candidates, setCandidates] = useState([]);

  // Save and error messages
  const [companyMessage, setCompanyMessage] = useState(null);
  const [HMMessage, setHMMessage] = useState(null);
  const [contractMessage, setContractMessage] = useState(null);
  const [messageType, setMessageType] = useState(null);
  const [contractValid, setContractValid] = useState(false);

  const clearInputs = () => {
    // Clear dependent fields
    setCompanyDetails({
      Name: "",
      Address: "",
      PhoneNumber: "",
      FinancialControllerName: "",
      FinancialControllerEmail: "",
      FinancialControllerNumber: "",
    });
    setSelectedHM(null);
    setHMDetails({ Name: "", Email: "", PhoneNumber: "", CompanyId: "" });
    setSelectedAlias(null);
    setSelectedContract(null);
    setContractDetails({
      JobId: "",
      CandidateId: "",
      ContractSigned: "",
      ContractCost: "",
      TotalContractCost: "",
      ContractorCost: "",
      ContractEndDate: "",
      WeeklyHoursTarget: 37.5,
      DailyHoursLimit: 7.5,
      Currency: "CAD"
    });
    setSelectedJob(null);
    setSelectedCandidate(null);
    setPostingDetails({
      CompanyId: "",
      JobId: "",
      ManagerId: "",
      AliasId: "",
    });
  };

  const clearContractInputs = () => {
    setSelectedAlias(null);
    setSelectedContract(null);
    setContractDetails({
      JobId: "",
      CandidateId: "",
      ContractSigned: "",
      ContractCost: "",
      TotalContractCost: "",
      ContractorCost: "",
      ContractEndDate: "",
      WeeklyHoursTarget: 37.5,
      DailyHoursLimit: 7.5,
      Currency: "CAD"
    });
    setSelectedJob(null);
    setSelectedCandidate(null);
    setPostingDetails({
      CompanyId: "",
      JobId: "",
      ManagerId: "",
      AliasId: "",
    });
  };

  useEffect(() => {
    var isContractValid =
      selectedContract &&
      contractDetails.JobId &&
      contractDetails.CandidateId &&
      contractDetails.ContractorCost &&
      contractDetails.ContractCost &&
      contractDetails.TotalContractCost &&
      contractDetails.WeeklyHoursTarget &&
      contractDetails.DailyHoursLimit &&
      contractDetails.ContractSigned !== "" &&
      contractDetails.ContractEndDate &&
      contractDetails.Currency !== "";

    setContractValid(isContractValid ?? false);
  }, [selectedContract, contractDetails]);


  const displaySuccessOrError = (button, message, messageType) => {
    setMessageType(messageType);

    if (button === "company") {
      setCompanyMessage(message);
      setTimeout(() => setCompanyMessage(""), 3000);

    } else if (button === "hm") {
      setHMMessage(message);
      setTimeout(() => setHMMessage(""), 3000);

    } else {
      setContractMessage(message);
      setTimeout(() => setContractMessage(""), 3000);

    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const companiesRes = await axios.get(API_ListCompanies);
        setCompanies(companiesRes.data);

        const hmsRes = await axios.get(API_ListHMs);
        setHiringManagers(hmsRes.data);

        const contractsRes = await axios.get(API_ListJobContracts);
        setJobContracts(contractsRes.data);

        const jobsRes = await axios.get(API_GetJobs);
        setJobs(jobsRes.data.filter(job => job.Status !== "Closed"));

        const candidatesRes = await axios.get(API_GetCandidates);
        setCandidates(candidatesRes.data);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      }
    };
    fetchDropdownData();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  };

  // Fetch & Update Company Info
  const handleCompanyChange = async (company) => {
    clearInputs();

    if (company?.Id === "new") {
      // Keep "Create New" selected without modifying the companies array
      setSelectedCompany("new");
      setCompanyDetails({
        Name: "",
        Address: "",
        PhoneNumber: "",
        FinancialControllerName: "",
        FinancialControllerEmail: "",
        FinancialControllerNumber: "",
      });

    } else {
      setSelectedCompany(company?.Id || null);
      if (company?.Id) {
        try {
          const response = await axios.get(`${API_GetCompanyInfo}/${company.Id}`);
          setCompanyDetails(response.data);
        } catch (error) {
          console.error("Error fetching company details:", error);
        }
      }
    }
  };

  const saveCompanyInfo = async () => {
    if (!validateEmail(companyDetails.FinancialControllerEmail)) {
      displaySuccessOrError(
        "company",
        "Financial controller's email is invalid.",
        "error"
      );
      return;
    }

    try {
      if (selectedCompany === "new") {
        const result = await axios.post(`${API_CreateCompany}`, companyDetails);
        const newID = result.data.insertId;

        setCompanies((prevCompanies) => prevCompanies.map((company) => company.Id === "new" ? { ...company, Id: newID, Name: companyDetails.Name } : company));
        setSelectedCompany(newID);
      } else {
        await axios.put(`${API_UpdateCompanyInfo}/${selectedCompany}`, companyDetails);
      }

      const updatedCompanies = await axios.get(API_ListCompanies);
      setCompanies(updatedCompanies.data);
      displaySuccessOrError("company", "Saved!", "success");

    } catch (error) {
      console.error("Error saving company info:", error);
      displaySuccessOrError("company", "An error has occurred.", "error");
    }
  };

  // Handle Selecting or Creating a New Hiring Manager
  const handleHMChange = async (hmId) => {
    clearContractInputs();

    if (hmId === "new") {
      setSelectedHM("new");
      setHMDetails({
        Name: "",
        Email: "",
        PhoneNumber: "",
        CompanyId: selectedCompany,
      });
    } else {
      setSelectedHM(hmId || null);
      if (hmId) {
        setHMDetails(hiringManagers.find((HM) => HM.Id === hmId) || {});
      }
    }
  };

  const saveHMInfo = async () => {
    if (!validateEmail(hmDetails.Email)) {
      displaySuccessOrError(
        "hm",
        "Hiring manager's email is invalid.",
        "error"
      );
      return;
    }

    // attach currently selected company to hiring manager
    setHMDetails((prevDetails) => ({
      ...prevDetails,
      CompanyId: selectedCompany,
    }));

    try {
      if (selectedHM === "new") {
        const result = await axios.post(`${API_CreateHM}`, { ...hmDetails, CompanyId: selectedCompany });
        const newID = result.data.insertId;

        setHiringManagers((prevHMs) => prevHMs.map((HM) => HM.Id === "new" ? { ...HM, Id: newID, Name: hmDetails.Name, CompanyId: selectedCompany } : HM));
        setSelectedHM(newID);

      } else {
        // Update existing HM
        await axios.put(`${API_UpdateHMInfo}/${selectedHM}`, { ...hmDetails, CompanyId: selectedCompany });
      }

      // Fetch the latest hiring managers list
      const updatedHMs = await axios.get(API_ListHMs);
      setHiringManagers(updatedHMs.data);
      displaySuccessOrError("hm", "Saved!", "success");
    } catch (error) {
      console.error("Error saving HM info:", error);
      displaySuccessOrError("hm", "An error has occurred.", "error");
    }
  };

  const handleJobChange = async (jobId) => {
    const companyPosting = await axios.get(`${API_GetCompanyPosting}/${jobId}`);
    setSelectedAlias(companyPosting.data?.AliasId || null);
    setSelectedJob(jobId);
    setContractDetails({ ...contractDetails, JobId: jobId });
  };

  const handleCandidateChange = async (candidateId) => {
    setSelectedCandidate(candidateId);
    setContractDetails({ ...contractDetails, CandidateId: candidateId });
  };

  const convertDateForDayJS = (dateToConvert) => {
    let date = new Date(dateToConvert);
    return `${date.getUTCFullYear()}-${("0" + (date.getUTCMonth() + 1)).slice(-2)}-${("0" + date.getUTCDate()).slice(-2)}`;
  }

  // Handle Selecting or Creating a New Job Contract
  const handleContractChange = async (contract) => {
    if (contract?.Id === "new") {
      clearContractInputs();
      setSelectedContract("new");
      setContractDetails({
        JobId: "",
        CandidateId: "",
        ContractSigned: "",
        ContractCost: "",
        TotalContractCost: "",
        ContractorCost: "",
        ContractEndDate: "",
        WeeklyHoursTarget: 37.5,
        DailyHoursLimit: 7.5,
        Currency: "CAD"
      });

      setPostingDetails({
        ...postingDetails,
        AliasId: "",
      });

    } else {
      const companyPosting = await axios.get(`${API_GetCompanyPosting}/${contract.JobId}`);
      if (companyPosting.data) setSelectedAlias(companyPosting.data?.AliasId || null);
      setSelectedContract(contract?.Id || null);

      if (contract?.Id) {
        setContractDetails({
          ...contract,
          ContractEndDate: contract.ContractEndDate ? dayjs(convertDateForDayJS(contract.ContractEndDate)).format("YYYY-MM-DD") : ""
        });

        setSelectedJob(contract.JobId);
        setSelectedCandidate(contract.CandidateId);
      }
    }
  };

  const saveContractInfo = async () => {
    try {
      const currentPostingDetails = {
        CompanyId: selectedCompany,
        JobId: selectedJob,
        ManagerId: selectedHM,
        AliasId: selectedAlias,
      };

      setPostingDetails(currentPostingDetails);

      const companyPosting = await axios.get(`${API_GetCompanyPosting}/${selectedJob}`);

      if (!companyPosting.data) {
        await axios.post(`${API_CreateCompanyPosting}`, currentPostingDetails);
      } else {
        await axios.put(`${API_UpdateCompanyPosting}/${selectedJob}`, currentPostingDetails);
      }

      if (selectedContract === "new") {
        const result = await axios.post(`${API_CreateJobContract}`, contractDetails);
        const newID = result.data.insertId;
        setJobContracts((prev) => prev.map((contract) => contract.Id === "new" ? { ...contract, Id: newID } : contract));
        setSelectedContract(newID);

      } else {
        await axios.put(`${API_UpdateJobContract}/${selectedContract}`, {
          ...contractDetails,
          ContractEndDate: contractDetails.ContractEndDate === "" ? null : contractDetails.ContractEndDate
        });
      }

      const updatedContracts = await axios.get(API_ListJobContracts);
      setJobContracts(updatedContracts.data);
      displaySuccessOrError("contract", "Saved!", "success");

    } catch (error) {
      console.error("Error saving contract info:", error);
      displaySuccessOrError("contract", "An error has occurred.", "error");
    }
  };

  if (!isAdmin) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="tgs-content">
      <div className="projectDetailsContainer">
        <h2>Manage Projects</h2>

        {/* Company Info */}
        <Paper className="form-section">
          <h3>Company Information</h3>

          {/* Searchable Company Dropdown */}
          <Autocomplete
            options={[{ Id: "new", Name: "Create New" }, ...companies]}
            getOptionLabel={(option) =>
              option?.Id === "new" ? "Create New" : option?.Name || ""
            }
            value={
              selectedCompany === "new"
                ? { Id: "new", Name: "Create New" }
                : companies.find((c) => c.Id === selectedCompany) || null
            }
            onChange={(event, newValue) => handleCompanyChange(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Select a Company" sx={{ m: 1 }} />
            )}
          />
          <div>
            <TextField
              label="Company Name"
              value={
                companyDetails.Name === "Create New" ? "" : companyDetails.Name
              }
              onChange={(e) =>
                setCompanyDetails({ ...companyDetails, Name: e.target.value })
              }
              sx={{ m: 1 }}
              disabled={!selectedCompany}
            />
            <TextField
              label="Company Address"
              value={companyDetails.Address}
              onChange={(e) =>
                setCompanyDetails({
                  ...companyDetails,
                  Address: e.target.value,
                })
              }
              sx={{ m: 1, minWidth: 500 }}
              disabled={!selectedCompany}
            />

            <PhoneNumberInput
              label="Phone Number"
              value={companyDetails.PhoneNumber}
              onChange={(newNumber) =>
                setCompanyDetails({ ...companyDetails, PhoneNumber: newNumber })
              }
              disabled={!selectedCompany}
              style={{ m: 1 }}
            />
          </div>

          <div>
            <TextField
              label="Financial Controller Name"
              value={companyDetails.FinancialControllerName}
              onChange={(e) =>
                setCompanyDetails({
                  ...companyDetails,
                  FinancialControllerName: e.target.value,
                })
              }
              sx={{ m: 1, minWidth: 220 }}
              disabled={!selectedCompany}
            />
            <TextField
              label="Financial Controller Email"
              value={companyDetails.FinancialControllerEmail}
              onChange={(e) =>
                setCompanyDetails({
                  ...companyDetails,
                  FinancialControllerEmail: e.target.value,
                })
              }
              sx={{ m: 1, minWidth: 220 }}
              disabled={!selectedCompany}
            />
            <PhoneNumberInput
              label={"Financial Controller Phone Number"}
              value={companyDetails.FinancialControllerNumber}
              onChange={(newNumber) =>
                setCompanyDetails({
                  ...companyDetails,
                  FinancialControllerNumber: newNumber,
                })
              }
              style={{ m: 1, minWidth: 280 }}
              disabled={!selectedCompany}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button onClick={saveCompanyInfo}>Save</Button>
            {companyMessage && (
              <Typography
                variant="body2"
                sx={{ color: messageType === "success" ? "green" : "red" }}
              >
                {companyMessage}
              </Typography>
            )}
          </div>
        </Paper>

        {/* Hiring Manager Info */}
        <Paper className="form-section">
          <h3>Hiring Managers</h3>
          <Autocomplete
            options={[{ Id: "new", Name: "Create New" }, ...(hiringManagers?.filter((manager) =>
              manager.Id !== selectedHM &&
              manager.CompanyId === selectedCompany) || [])
            ]}
            getOptionLabel={(option) =>
              option?.Id === "new" ? "Create New" : option?.Name || ""
            }
            value={
              selectedHM === "new"
                ? { Id: "new", Name: "Create New" }
                : hiringManagers.find((hm) => hm.Id === selectedHM) || null
            }
            onChange={(event, newValue) => handleHMChange(newValue?.Id || null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select a Hiring Manager"
                sx={{ m: 1 }}
              />
            )}
            disabled={!selectedCompany}
          />
          <div>
            <TextField
              label="Hiring Manager Name"
              value={hmDetails.Name === "Create New" ? "" : hmDetails.Name}
              onChange={(e) =>
                setHMDetails({ ...hmDetails, Name: e.target.value })
              }
              sx={{ m: 1 }}
              disabled={!selectedHM}
            />
            <TextField
              label="Hiring Manager Email"
              value={hmDetails.Email}
              onChange={(e) =>
                setHMDetails({ ...hmDetails, Email: e.target.value })
              }
              sx={{ m: 1 }}
              disabled={!selectedHM}
            />
            <PhoneNumberInput
              label="Hiring Manager Phone Number"
              value={hmDetails.PhoneNumber}
              onChange={(newNumber) =>
                setHMDetails({ ...hmDetails, PhoneNumber: newNumber })
              }
              disabled={!selectedHM}
              style={{ m: 1, minWidth: 260 }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button onClick={saveHMInfo} disabled={!selectedHM}>
              Save
            </Button>
            {HMMessage && (
              <Typography
                variant="body2"
                sx={{ color: messageType === "success" ? "green" : "red" }}
              >
                {HMMessage}
              </Typography>
            )}
          </div>
        </Paper>

        {/* Job Contract Info */}
        <Paper className="form-section">
          <h3>Job Contract</h3>
          {/* Searchable Contract Dropdown */}
          <Autocomplete
            options={[{ Id: "new", JobId: "Create New" }, ...jobContracts]}
            getOptionLabel={(option) => {
              if (option?.Id === "new") return "Create New";
              const job = jobs?.find((job) => job.Id === option.JobId);
              const candidate = candidates.find((c) => c.Id === option.CandidateId);
              return `${option.Id} - ${job?.Title || "Unknown Job"} - ${candidate?.Name || "Unknown Candidate"}`;
            }}
            value={selectedContract === "new" ? { Id: "new", JobId: "Create New" } : jobContracts.find((contract) => contract.Id === selectedContract) || null}
            onChange={(event, newValue) => handleContractChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Job Contract"
                sx={{ m: 1 }}
              />
            )}
            disabled={!selectedHM}
          />
          <div>
            {/* Searchable Job Dropdown */}
            <Autocomplete
              options={jobs || []}
              getOptionLabel={(option) =>
                `${option?.Id} - ${option?.Title}` || ""
              }
              value={jobs?.find((job) => job.Id === selectedJob) || null}
              onChange={(e, newValue) => handleJobChange(newValue?.Id || null)}
              renderInput={(params) => (
                <TextField {...params} label="Select Job" sx={{ m: 1 }} />
              )}
              disabled={!selectedHM}
            />
          </div>

          <TextField
            label="Hiring Manager"
            value={
              selectedHM
                ? hiringManagers.find((hm) => hm.Id === selectedHM)?.Name ||
                "No manager selected"
                : "No manager selected"
            }
            sx={{ m: 1, minWidth: 260 }}
            disabled
          />

          {/* Searchable Candidate Dropdown */}
          <div>
            <Autocomplete
              options={candidates || []}
              getOptionLabel={(option) =>
                `${option?.Id} - ${option?.Name}` || ""
              }
              value={
                candidates?.find(
                  (candidate) => candidate.Id === selectedCandidate
                ) || null
              }
              onChange={(e, newValue) =>
                handleCandidateChange(newValue?.Id || null)
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Candidate" sx={{ m: 1 }} />
              )}
              disabled={!selectedHM}
            />
          </div>

          {/* Searchable Alias Manager Dropdown */}
          <div>
            <Autocomplete
              options={
                hiringManagers?.filter(
                  (manager) =>
                    manager.Id !== selectedHM &&
                    manager.CompanyId === selectedCompany
                ) || []
              }
              getOptionLabel={(option) => option?.Name}
              value={
                selectedAlias
                  ? hiringManagers.find(
                    (manager) => manager.Id === selectedAlias
                  ) || null
                  : null
              }
              onChange={(e, newValue) =>
                setSelectedAlias(
                  newValue?.Id === selectedHM ? null : newValue?.Id || null
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Alias Manager"
                  sx={{ m: 1 }}
                />
              )}
              disabled={selectedHM === null}
            />
          </div>
          <div>
            <FormControl
              sx={{ m: 1, minWidth: 170 }}
              disabled={selectedContract === null}
            >
              <InputLabel id="contract-signed-label" shrink>
                Contract Signed
              </InputLabel>
              <Select
                labelId="contract-signed-label"
                id="contract-signed"
                label="Contract Signed"
                value={
                  contractDetails.ContractSigned === 1
                    ? "Yes"
                    : contractDetails.ContractSigned === 0
                      ? "No"
                      : ""
                }
                onChange={(e) =>
                  setContractDetails({
                    ...contractDetails,
                    ContractSigned: e.target.value === "Yes" ? 1 : 0,
                  })
                }
                disabled={selectedContract === null}
                displayEmpty
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Contractor Cost (Hourly)"
              value={contractDetails.ContractorCost}
              onChange={(e) =>
                setContractDetails({
                  ...contractDetails,
                  ContractorCost: e.target.value,
                })
              }
              sx={{ m: 1 }}
              disabled={selectedContract === null}
            />
            <TextField
              label="Billing Cost (Hourly)"
              value={contractDetails.ContractCost}
              onChange={(e) =>
                setContractDetails({
                  ...contractDetails,
                  ContractCost: e.target.value,
                })
              }
              sx={{ m: 1 }}
              disabled={selectedContract === null}
            />
            <TextField
              label="Contract End Date"
              type="date"
              value={
                contractDetails.ContractEndDate
                  ? dayjs(convertDateForDayJS(contractDetails.ContractEndDate)).format(
                    "YYYY-MM-DD"
                  )
                  : null
              }
              onChange={(e) =>
                setContractDetails({
                  ...contractDetails,
                  ContractEndDate: e.target.value,
                })
              }
              sx={{ m: 1 }}
              disabled={selectedContract === null}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Maximum Weekly Hours"
              value={contractDetails.WeeklyHoursTarget}
              onChange={(e) =>
                setContractDetails({
                  ...contractDetails,
                  WeeklyHoursTarget: e.target.value,
                })
              }
              sx={{ m: 1, minWidth: 200 }}
              disabled={selectedContract === null}
            />
            <TextField
              label="Maximum Daily Hours"
              value={contractDetails.DailyHoursLimit}
              onChange={(e) =>
                setContractDetails({
                  ...contractDetails,
                  DailyHoursLimit: e.target.value,
                })
              }
              sx={{ m: 1, minWidth: 200 }}
              disabled={selectedContract === null}
              InputLabelProps={{ shrink: true }}
              type="number"
            />

            <TextField
              label="Total Contract Cost"
              value={contractDetails.TotalContractCost}
              onChange={(e) =>
                setContractDetails({
                  ...contractDetails,
                  TotalContractCost: e.target.value,
                })
              }
              sx={{ m: 1 }}
              disabled={selectedContract === null}
            />

            <FormControl
              sx={{ m: 1, minWidth: 170 }}
              disabled={selectedContract === null}
            >
              <InputLabel id="currency-label" shrink>
                Currency
              </InputLabel>

              <Select
                labelId="currency-label"
                id="currency"
                label="Currency"
                value={contractDetails.Currency}
                onChange={(e) =>
                  setContractDetails({
                    ...contractDetails,
                    Currency: e.target.value,
                  })
                }
                disabled={selectedContract === null}
                displayEmpty
              >
                <MenuItem value="">Select...</MenuItem>
                <MenuItem value="CAD">CAD</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
                <MenuItem value="INR">INR</MenuItem>
              </Select>
            </FormControl>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Button onClick={saveContractInfo} disabled={!contractValid}>
              Save
            </Button>
            {contractMessage && (
              <Typography
                variant="body2"
                sx={{ color: messageType === "success" ? "green" : "red" }}
              >
                {contractMessage}
              </Typography>
            )}
          </div>
        </Paper>
      </div>
    </div>
  );
};
